import { BADGES } from '../lib/badges.js'
import HelpTooltip from './HelpTooltip.jsx'

export default function BadgeShelf({ unlockedSlugs = [], compact = false }) {
  const set = new Set(unlockedSlugs)
  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <h4 className="text-xs uppercase tracking-wider font-semibold text-brand-text-muted">Badges</h4>
        <HelpTooltip label="How badges work">
          Unlock automatically by hitting milestones. No point value — bragging rights only. Hover any badge to see how to earn it.
        </HelpTooltip>
      </div>
      <div className={`grid ${compact ? 'grid-cols-4' : 'grid-cols-4 sm:grid-cols-8'} gap-2`}>
        {BADGES.map(b => {
          const unlocked = set.has(b.slug)
          return <Badge key={b.slug} badge={b} unlocked={unlocked} />
        })}
      </div>
    </div>
  )
}

/**
 * Single badge tile with a custom-styled hover tooltip. We deliberately don't
 * use the browser's native title= because the delay is awful and you can't
 * style it. The tooltip lives in the same stacking context as the tile and
 * uses `group-hover` so it appears on hover/focus.
 *
 * `pointer-events-none` on the tooltip stops it from blocking the cursor and
 * causing flicker. z-30 keeps it above neighbour tiles but below modals.
 */
function Badge({ badge, unlocked }) {
  return (
    <div
      tabIndex={0}
      aria-label={`${badge.name}: ${badge.description}. ${unlocked ? 'Unlocked.' : `How to earn: ${badge.howTo}`}`}
      className={`group relative aspect-square rounded-xl flex items-center justify-center text-2xl border transition-all cursor-help focus:outline-none focus:ring-2 focus:ring-brand-blue ${
        unlocked
          ? 'bg-white border-brand-blue shadow-card hover:shadow-card-hover hover:scale-105'
          : 'bg-brand-bg-muted border-brand-border grayscale opacity-40 hover:opacity-70'
      }`}
    >
      {badge.emoji}

      <div
        role="tooltip"
        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-2 w-56 z-30 opacity-0 translate-y-1 group-hover:opacity-100 group-hover:translate-y-0 group-focus:opacity-100 group-focus:translate-y-0 transition-all"
      >
        <div className="bg-brand-text text-white text-left text-[11px] leading-snug rounded-lg shadow-xl px-3 py-2.5 space-y-1.5">
          <div className="flex items-center gap-1.5">
            <span className="text-sm leading-none">{badge.emoji}</span>
            <span className="font-semibold text-white text-xs">{badge.name}</span>
            {unlocked && (
              <span className="ml-auto text-[9px] uppercase tracking-wider text-brand-success bg-white/10 px-1.5 py-0.5 rounded">
                Unlocked
              </span>
            )}
          </div>
          <div className="text-white/70 italic">{badge.description}</div>
          <div className="border-t border-white/15 pt-1.5">
            <div className="text-[9px] uppercase tracking-wider text-white/50 mb-0.5">
              {unlocked ? 'You earned this by' : 'How to earn'}
            </div>
            <div className="text-white">{badge.howTo}</div>
          </div>
        </div>
        <div className="absolute top-full left-1/2 -translate-x-1/2 -mt-1 w-2 h-2 bg-brand-text rotate-45" />
      </div>
    </div>
  )
}
